/**
 * WalletExclusionManager - CRUD for team/treasury wallet exclusions
 */

import React, { useState } from 'react';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { FeatherPlus, FeatherTrash2 } from '@subframe/core';
import {
  useWalletExclusions,
  useCreateWalletExclusion,
  useDeleteWalletExclusion,
} from '../../hooks/useDistribution';

interface WalletExclusionManagerProps {
  projectSlug: string;
}

export const WalletExclusionManager: React.FC<WalletExclusionManagerProps> = ({ projectSlug }) => {
  const { data, isLoading } = useWalletExclusions(projectSlug);
  const createMutation = useCreateWalletExclusion(projectSlug);
  const deleteMutation = useDeleteWalletExclusion(projectSlug);

  const [showForm, setShowForm] = useState(false);
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [excludeFrom, setExcludeFrom] = useState('');
  const [excludeUntil, setExcludeUntil] = useState('');

  const exclusions = data?.exclusions || [];

  const resetForm = () => {
    setAddress('');
    setLabel('');
    setExcludeFrom('');
    setExcludeUntil('');
    setShowForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!address) return;

    createMutation.mutate(
      {
        xrpl_address: address,
        label: label || undefined,
        exclude_from: excludeFrom || undefined,
        exclude_until: excludeUntil || undefined,
      },
      { onSuccess: resetForm }
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="text-body-bold font-body-bold text-default-font">Wallet Exclusions</span>
        {!showForm && (
          <Button variant="brand-secondary" size="small" icon={<FeatherPlus />} onClick={() => setShowForm(true)}>
            Add Exclusion
          </Button>
        )}
      </div>

      <p className="text-caption font-caption text-subtext-color">
        Excluded wallets will not receive reward distributions. Use this for team wallets, treasury, or known bots.
      </p>

      {showForm && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 rounded border border-solid border-neutral-border p-4">
          <TextField className="h-auto w-full" label="XRPL Address">
            <TextField.Input
              placeholder="rABC...XYZ"
              value={address}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value)}
            />
          </TextField>
          <TextField className="h-auto w-full" label="Label (optional)">
            <TextField.Input
              placeholder="e.g., Team treasury"
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
            />
          </TextField>
          <div className="flex gap-3">
            <TextField className="h-auto flex-1" label="Exclude From (optional)">
              <TextField.Input type="date" value={excludeFrom} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExcludeFrom(e.target.value)} />
            </TextField>
            <TextField className="h-auto flex-1" label="Exclude Until (optional)">
              <TextField.Input type="date" value={excludeUntil} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExcludeUntil(e.target.value)} />
            </TextField>
          </div>
          {createMutation.error && (
            <span className="text-caption font-caption text-red-600">{createMutation.error.message}</span>
          )}
          <div className="flex gap-2">
            <Button type="submit" variant="brand-primary" size="small" disabled={createMutation.isPending}>Add</Button>
            <Button variant="neutral-secondary" size="small" onClick={resetForm}>Cancel</Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="text-subtext-color py-4">Loading exclusions...</div>
      ) : exclusions.length === 0 ? (
        <div className="text-subtext-color py-4">No wallet exclusions configured.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-body font-body">
            <thead>
              <tr className="border-b border-neutral-border text-left text-caption font-caption text-subtext-color">
                <th className="px-2 py-1">Address</th>
                <th className="px-2 py-1">Label</th>
                <th className="px-2 py-1">Date Range</th>
                <th className="px-2 py-1 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {exclusions.map(exc => (
                <tr key={exc.id} className="border-b border-neutral-border">
                  <td className="px-2 py-1 font-mono text-caption text-default-font">
                    {exc.xrpl_address.slice(0, 8)}...{exc.xrpl_address.slice(-6)}
                  </td>
                  <td className="px-2 py-1 text-default-font">{exc.label || '-'}</td>
                  <td className="px-2 py-1 text-subtext-color">
                    {exc.exclude_from || exc.exclude_until
                      ? `${exc.exclude_from ? new Date(exc.exclude_from).toLocaleDateString() : 'start'} - ${exc.exclude_until ? new Date(exc.exclude_until).toLocaleDateString() : 'ongoing'}`
                      : 'Always'}
                  </td>
                  <td className="px-2 py-1">
                    <button onClick={() => deleteMutation.mutate(exc.id)} className="p-1 rounded hover:bg-red-50">
                      <FeatherTrash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
